import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckoutOverlay } from "@/components/CheckoutOverlay";
import { useAuth } from "@/context/AuthContext";
import { BrandMenu } from "@/components/BrandMenu";
import { Sparkles, Plus, LogOut, Calendar, ArrowRight, Baby, GraduationCap, Gift, Trash2, Shield } from "lucide-react";

const TYPE_ICON = { baby: Baby, exam: GraduationCap, custom: Gift };
const REVEAL_COLORS = ["#D4AF37", "#3B82F6", "#EC4899", "#22C55E", "#8B5CF6", "#EF4444", "#F97316", "#06B6D4"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  const [revealColor, setRevealColor] = useState("#D4AF37");
  const [scheduledAt, setScheduledAt] = useState("");
  const [message, setMessage] = useState("");
  const [maxGuests, setMaxGuests] = useState(10);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  const load = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (e, ev) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${ev.title}"? This removes the reveal room and its reactions. This can't be undone.`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      setEvents((prev) => prev.filter((x) => x.id !== ev.id));
      toast.success("Reveal deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete reveal");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!scheduledAt) { toast.error("Please pick a date & time for the reveal"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/events/checkout", {
        title, reveal_type: "custom", reveal_value: value, reveal_label: "", reveal_color: revealColor,
        scheduled_at: new Date(scheduledAt).toISOString(), message, max_guests: Number(maxGuests),
      });
      setOpen(false);
      setCheckoutUrl(data.checkout_url);
      setLoading(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandMenu />
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground/60 hidden sm:block">{user?.name || user?.email}</span>
            {user?.role === "admin" && (
              <Button data-testid="nav-admin-btn" variant="outline" size="sm" onClick={() => navigate("/admin")} className="rounded-full border-gold/40 text-gold bg-transparent hover:bg-gold/10">
                <Shield className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Admin</span>
              </Button>
            )}
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={() => { logout(); navigate("/"); }} className="rounded-full border-white/20 bg-transparent">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-2">Your reveals</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Reveal dashboard</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-reveal-btn" className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 font-semibold">
                <Plus className="w-4 h-4 mr-2" /> New reveal
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Create a reveal</DialogTitle>
                <DialogDescription className="text-foreground/50">Set up your reveal, pick a guest count, then pay to unlock the room.</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-5 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-foreground/60">Event title</Label>
                  <Input data-testid="event-title-input" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Our big surprise" className="h-12 mt-2 bg-input border-white/10 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-foreground/60">Date &amp; time (room opens then, lasts 15 min)</Label>
                  <Input data-testid="event-schedule-input" type="datetime-local" required value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="h-12 mt-2 bg-input border-white/10 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-foreground/60">Secret</Label>
                  <Input data-testid="secret-input" value={value} onChange={(e) => setValue(e.target.value)} required placeholder="It's a Boy!" className="h-12 mt-2 bg-input border-white/10 rounded-xl" />
                  <p className="mt-2 text-sm text-foreground/60">
                    This is the big message everyone sees the instant the reveal countdown hits zero — so make it count! For example: <span className="text-foreground/90 font-medium">It's a Boy!</span>, <span className="text-foreground/90 font-medium">It's a Girl!</span>, <span className="text-foreground/90 font-medium">We Passed!</span> or <span className="text-foreground/90 font-medium">We're Engaged!</span>
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-foreground/60">Message shown after reveal</Label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Baby arriving this December — we can't wait!" className="mt-2 bg-input border-white/10 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-foreground/60">Reveal colour</Label>
                  <div className="flex flex-wrap items-center gap-2.5 mt-2">
                    {REVEAL_COLORS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        data-testid={`reveal-color-${c.replace("#", "").toLowerCase()}`}
                        onClick={() => setRevealColor(c)}
                        aria-label={`Colour ${c}`}
                        className={`w-9 h-9 rounded-full transition-transform duration-150 ${revealColor.toLowerCase() === c.toLowerCase() ? "ring-2 ring-white ring-offset-2 ring-offset-card scale-110" : "hover:scale-110"}`}
                        style={{ background: c }}
                      />
                    ))}
                    <label className="relative w-9 h-9 rounded-full overflow-hidden cursor-pointer border border-white/25 shrink-0" title="Pick any colour">
                      <span className="block w-full h-full" style={{ background: "conic-gradient(red, orange, yellow, lime, cyan, blue, magenta, red)" }} />
                      <input
                        type="color"
                        data-testid="reveal-color-wheel"
                        value={revealColor}
                        onChange={(e) => setRevealColor(e.target.value)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </label>
                  </div>
                  <p className="mt-2 text-sm text-foreground/60">
                    Your secret shows in this colour. Preview:{" "}
                    <span data-testid="reveal-color-preview" className="font-display font-black" style={{ color: revealColor }}>{value || "It's a Boy!"}</span>
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-foreground/60">Maximum guests</Label>
                  <Input data-testid="max-guests-input" type="number" min={2} max={50} required value={maxGuests}
                    onChange={(e) => setMaxGuests(e.target.value)} className="h-12 mt-2 bg-input border-white/10 rounded-xl" />
                  <p className="mt-2 text-sm text-foreground/60">$1.00 per guest · extend later for $0.25/guest per 5 min.</p>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-gold/10 border border-gold/30 px-4 py-3">
                  <span className="text-sm text-foreground/70">Total due now</span>
                  <span className="font-impact text-2xl text-gold" data-testid="checkout-total">${(Number(maxGuests || 0) * 1).toFixed(2)}</span>
                </div>
                <Button data-testid="create-event-submit-btn" type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-gold hover:bg-gold/90 text-black font-semibold">
                  {loading ? "Redirecting to payment…" : `Pay $${(Number(maxGuests || 0) * 1).toFixed(2)} & create reveal`}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-white/10 rounded-3xl" data-testid="empty-state">
            <Sparkles className="w-10 h-10 text-gold mx-auto mb-4" />
            <p className="text-foreground/60">No reveals yet. Create your first one!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev, i) => {
              const Icon = TYPE_ICON[ev.reveal_type] || Gift;
              return (
                <motion.div key={ev.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  data-testid={`event-card-${ev.id}`} onClick={() => navigate(`/event/${ev.id}`)}
                  className="cursor-pointer rounded-2xl bg-card border border-white/5 p-6 hover:-translate-y-1 hover:border-white/20 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center"><Icon className="w-5 h-5 text-gold" /></div>
                    <div className="flex items-center gap-2">
                      {ev.revealed ? <span className="text-xs px-3 py-1 rounded-full bg-forest/15 text-forest font-semibold">Revealed</span>
                        : <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-foreground/70 font-semibold">Pending</span>}
                      <button data-testid={`delete-event-btn-${ev.id}`} onClick={(e) => remove(e, ev)} title="Delete reveal"
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-xl mb-1">{ev.title}</h3>
                  {ev.scheduled_at ? (
                    <p className="text-sm text-foreground/50 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(ev.scheduled_at).toLocaleString()}</p>
                  ) : <p className="text-sm text-foreground/50">No date set</p>}
                  <div className="mt-5 flex items-center text-gold text-sm font-semibold">Open <ArrowRight className="w-4 h-4 ml-1" /></div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <CheckoutOverlay url={checkoutUrl} onClose={() => { setCheckoutUrl(null); load(); }} />
    </div>
  );
}
